// src/components/modals/TermsModal.jsx
import { useEffect, useRef } from "react";
import { X } from "lucide-react";

const TermsModal = ({ open, onClose, data }) => {
  const modalRef = useRef(null);

  // ESC close
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // body scroll lock
  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-title"
      onMouseDown={(e) => {
        // click outside to close
        if (modalRef.current && !modalRef.current.contains(e.target)) {
          onClose?.();
        }
      }}
    >
      <div className="absolute inset-0 bg-black/50" />

      <div
        ref={modalRef}
        className="relative z-10 w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div className="pr-4">
            <h3 id="terms-title" className="text-lg font-bold text-gray-900">
              {data?.title || "Terms & Conditions"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Effective Date: {data?.meta?.effectiveDate || "-"} • Country of
              Operation: {data?.meta?.country || "-"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-full p-2 text-gray-600 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            {Array.isArray(data?.sections) &&
              data.sections.map((sec, idx) => (
                <section key={idx}>
                  <h4 className="font-semibold text-gray-900">{sec.heading}</h4>
                  <p className="mt-1">{sec.body}</p>

                  {/* Contact block appears after section 14 */}
                  {sec.heading?.startsWith("14.") && data?.contact && (
                    <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                      <p className="font-semibold text-gray-900">
                        {data.contact.company}
                      </p>
                      <p>Email: {data.contact.email}</p>
                      <p>Phone: {data.contact.phone}</p>
                      <p>Address: {data.contact.address}</p>
                    </div>
                  )}
                </section>
              ))}

            {data?.footerNote && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                {data.footerNote}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-800 hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;